import { PRESENTATION_FILTER_LABEL, levelFilterLabel } from '@/lib/persona-labels';
import { PERSONA_WORDING } from '@/lib/persona-wording';
import type {
  VersionedCounts,
  VersionedFilterChip,
  VersionedListSpec,
  VersionedSearch,
} from '@/lib/versioned-filters';
import {
  activeVersionedFilters,
  filterVersioned,
  hasActiveVersionedFilters,
  hasHiddenVersionedFilter,
  readVersionedFilters,
  resetVersionedFilters,
  versionedAuthors,
  versionedCountLabel,
  versionedCounts,
  versionedFiltersOf,
  versionedSearchOf,
} from '@/lib/versioned-filters';
import type { InvolvementLevel, PersonaCard, PersonaFilters } from '@matter_extractor/shared';
import {
  PERSONA_FILTERS_DEFAULT,
  PERSONA_LEVEL_FILTER_ALL,
  PERSONA_LEVEL_FILTER_RETIRED,
  personaPresentationFilterSchema,
} from '@matter_extractor/shared';

/**
 * What the Personas list adds to the common rule set of §4 (refonte modulaire
 * §10, §15).
 *
 * Thirteen of the filters, the four sorts, the sparse URL, the chips and the
 * counting are in `versioned-filters.ts`, and every versioned object gets them.
 * A persona adds exactly TWO dimensions, both of them filters:
 *
 *   - **présentation** - anonyme or nommée, which is « qui parle » ;
 *   - **cran** - one cran of the current scale, or « cran retiré », which finds
 *     the personas whose `levelId` no longer matches anything and which the
 *     card deliberately does not mark (§15).
 *
 * And **no sort of its own** (§15): neither facet orders anything an operator
 * would look for - « les personas les plus impliqués » is a filter, not a
 * ranking - and a sort nobody asked for is a control everybody reads past.
 *
 * ## Why the spec is a FACTORY, where the criterion's is a constant
 *
 * The cran filter's options ARE the rows of a table an operator edits (§11.5),
 * so its chip cannot be worded without the scale: « Cran : réciproque » needs
 * the name behind the identifier, and `VersionedListSpec.chips` is handed the
 * filters and nothing else. `personaListSpec(levels)` closes over the scale the
 * page has already read for its select, and every function below stays pure.
 */

/** Does this card survive the presentation filter? */
function matchesPresentation(card: PersonaCard, filters: PersonaFilters): boolean {
  switch (filters.presentation) {
    case 'all':
      return true;
    case 'anonymous':
      return card.presentation.kind === 'anonymous';
    case 'named':
      return card.presentation.kind === 'named';
  }
}

/**
 * Does this card survive the cran filter?
 *
 * `retired` reads the flag the SERVER computed against the current scale
 * (`personaCardOf`), and not « the id is missing from the list I hold »: the
 * two agree, and taking the server's keeps one answer to one question.
 */
function matchesLevel(card: PersonaCard, filters: PersonaFilters): boolean {
  if (filters.level === PERSONA_LEVEL_FILTER_ALL) return true;
  if (filters.level === PERSONA_LEVEL_FILTER_RETIRED) return card.level.levelRetired;
  return card.level.levelId === filters.level;
}

export function personaListSpec(
  levels: readonly InvolvementLevel[],
): VersionedListSpec<PersonaCard, PersonaFilters> {
  return {
    defaults: PERSONA_FILTERS_DEFAULT,

    read: (search) => {
      const patch: Partial<PersonaFilters> = {};
      const presentation = personaPresentationFilterSchema.safeParse(search.presentation);
      if (presentation.success && presentation.data !== PERSONA_FILTERS_DEFAULT.presentation) {
        patch.presentation = presentation.data;
      }
      /*
        The cran arrives as a BARE STRING and is kept as one, unlike the two
        referent filters of §4 which drop what they cannot resolve. It cannot be
        checked here: `validateSearch` runs before any query, so the scale is not
        readable yet - and dropping every unknown value would drop the legitimate
        one too, on the very first paint of a pasted link. An identifier that has
        since left the scale therefore survives into the chip, which words it
        honestly, and the grid is empty rather than wrong.
      */
      const level = search.level;
      if (typeof level === 'string' && level !== '' && level !== PERSONA_LEVEL_FILTER_ALL) {
        patch.level = level;
      }
      return patch;
    },

    write: (filters) => {
      const search: Partial<PersonaFilters> = {};
      if (filters.presentation !== PERSONA_FILTERS_DEFAULT.presentation) {
        search.presentation = filters.presentation;
      }
      if (filters.level !== PERSONA_LEVEL_FILTER_ALL) search.level = filters.level;
      return search;
    },

    matches: (card, filters) => matchesPresentation(card, filters) && matchesLevel(card, filters),

    chips: (filters): VersionedFilterChip<PersonaFilters>[] => {
      const chips: VersionedFilterChip<PersonaFilters>[] = [];
      if (filters.presentation !== 'all') {
        chips.push({
          id: 'presentation',
          label: `Présentation : ${PRESENTATION_FILTER_LABEL[filters.presentation].toLowerCase()}`,
          patch: { presentation: 'all' },
        });
      }
      if (filters.level !== PERSONA_LEVEL_FILTER_ALL) {
        chips.push({
          id: 'level',
          label: `Cran : ${levelFilterLabel(filters.level, levels).toLowerCase()}`,
          patch: { level: PERSONA_LEVEL_FILTER_ALL },
        });
      }
      return chips;
    },

    /** None: the four common sorts are all a persona orders by (§15). */
    sorts: {},
  };
}

/* ------------------------------------------------ the layer, bound to §10 */

/**
 * The layer's rules with the persona's spec already supplied.
 *
 * The same shape as the criterion's bindings, one argument longer: the scale
 * travels with every call because the spec is built from it. A caller writes
 * `filterPersonas(cards, filters, levels)` rather than
 * `filterVersioned(cards, filters, personaListSpec(levels))` eight times on one
 * screen.
 */

export type PersonaSearch = VersionedSearch<PersonaFilters>;
export type { VersionedCounts as PersonaCounts };

/**
 * The six bindings below hand the spec an EMPTY scale, and that is safe by
 * construction rather than by luck: each of them reaches only `defaults`,
 * `read`, `write`, or the LENGTH of `chips`. None reads a cran's name, which is
 * the single thing the scale is there for. The three that do - the chips of the
 * summary, the filtering, the counting - take it as an argument.
 *
 * `readPersonaFilters` is the reason the split exists at all: it is the route's
 * `validateSearch`, so it runs before any query resolves. That is also why
 * `read` above keeps an unresolvable cran rather than dropping it.
 */
export const readPersonaFilters = (search: Record<string, unknown>): PersonaSearch =>
  readVersionedFilters(search, personaListSpec([]));

export const personaFiltersOf = (search: PersonaSearch): PersonaFilters =>
  versionedFiltersOf(search, personaListSpec([]));

export const personaSearchOf = (filters: PersonaFilters): PersonaSearch =>
  versionedSearchOf(filters, personaListSpec([]));

export const filterPersonas = (
  cards: readonly PersonaCard[],
  filters: PersonaFilters,
  levels: readonly InvolvementLevel[],
): PersonaCard[] => filterVersioned(cards, filters, personaListSpec(levels));

export const personaCounts = (
  cards: readonly PersonaCard[],
  filters: PersonaFilters,
  levels: readonly InvolvementLevel[],
): VersionedCounts => versionedCounts(cards, filters, personaListSpec(levels));

/** « 6 personas » with nothing filtered, « 2 personas sur 6 » with something (§4). */
export const personaCountLabel = (counts: VersionedCounts): string =>
  versionedCountLabel(counts, PERSONA_WORDING.noun);

export const personaAuthors = versionedAuthors;

export const activePersonaFilters = (
  filters: PersonaFilters,
  levels: readonly InvolvementLevel[],
): VersionedFilterChip<PersonaFilters>[] =>
  activeVersionedFilters(filters, personaListSpec(levels));

export const hasActivePersonaFilters = (filters: PersonaFilters): boolean =>
  hasActiveVersionedFilters(filters, personaListSpec([]));

export const hasHiddenPersonaFilter = (filters: PersonaFilters): boolean =>
  hasHiddenVersionedFilter(filters, personaListSpec([]));

export const resetPersonaFilters = (filters: PersonaFilters): PersonaFilters =>
  resetVersionedFilters(filters, personaListSpec([]));
